import React, { useEffect, useMemo, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import API from '../../../services/API'

const buildQuery = (address = {}) =>
  [
    address.name,
    address.addressLine1,
    address.addressLine2,
    address.town,
    address.city,
    address.postCode,
  ]
    .filter(Boolean)
    .join(', ')

const AddressAutocomplete = ({
  label = 'Find address or postcode',
  address = {},
  onSelect,
  helperText = 'Start typing an address, hospital name, or postcode.',
}) => {
  const [inputValue, setInputValue] = useState(buildQuery(address))
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [resolving, setResolving] = useState(false)

  const selectedLocationText = useMemo(() => {
    const coords = address?.location?.coordinates
    if (!Array.isArray(coords) || coords.length !== 2) return ''
    return `Selected map point: ${coords[1].toFixed(5)}, ${coords[0].toFixed(5)}`
  }, [address?.location?.coordinates])

  useEffect(() => {
    setInputValue(buildQuery(address))
  }, [
    address?.name,
    address?.addressLine1,
    address?.addressLine2,
    address?.town,
    address?.city,
    address?.postCode,
  ])

  useEffect(() => {
    const query = inputValue.trim()

    if (query.length < 3) {
      setOptions([])
      setLoading(false)
      return undefined
    }

    setLoading(true)
    const timeout = setTimeout(async () => {
      try {
        const res = await API.get('/pts/address-search', {
          params: { query },
          skipGlobalErrorHandler: true,
        })
        setOptions(res.status === 200 ? res.data || [] : [])
      } catch (e) {
        setOptions([])
      } finally {
        setLoading(false)
      }
    }, 350)

    return () => clearTimeout(timeout)
  }, [inputValue])

  return (
    <Autocomplete
      fullWidth
      filterOptions={(items) => items}
      options={options}
      loading={loading}
      value={null}
      inputValue={inputValue}
      onInputChange={(event, value, reason) => {
        if (reason === 'reset') return
        setInputValue(value)
      }}
      onChange={async (event, option) => {
        if (!option) return

        if (!option.requiresResolve) {
          onSelect(option)
          setInputValue(option.label || buildQuery(option))
          return
        }

        setResolving(true)
        try {
          const res = await API.get('/pts/address-resolve', {
            params: {
              id: option.id,
              provider: option.provider,
              query: option.label,
            },
            skipGlobalErrorHandler: true,
          })

          if (res.status === 200) {
            onSelect(res.data)
            setInputValue(res.data.label || option.label || buildQuery(res.data))
          }
        } finally {
          setResolving(false)
        }
      }}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      noOptionsText={
        inputValue.trim().length < 3 ? 'Type at least 3 characters' : 'No matching addresses'
      }
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <div>
            <Typography variant="body2" fontWeight={700}>
              {option.name || option.addressLine1 || option.postCode}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.label}
            </Typography>
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={selectedLocationText || helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading || resolving ? (
                  <CircularProgress color="inherit" size={18} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

export default AddressAutocomplete
